import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Storage } from '@ionic/storage-angular';
import { rememberTheMapLanguage } from '../domains/app/the-map-speaks-the-language-of-the-app';
import { captureException } from '../../tracing';

export interface Locale {
  code: string;
  name: string;
  lang: string;
  /** Shown on the language screen, next to the name. */
  flag: string;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  public readonly locales: Locale[] = [
    { code: 'fr-FR', name: 'USER_PROFILE.LANGUAGE.FRENCH', lang: 'fr', flag: '🇫🇷' },
    { code: 'en-US', name: 'USER_PROFILE.LANGUAGE.ENGLISH', lang: 'en', flag: '🇬🇧' },
  ];
  private storage: Storage = inject(Storage);
  public get locale(): Locale {
    return this._locale ?? this.locales[0];
  }

  private readonly LANGUAGE_KEY = 'preferred_language';
  private _locale: Locale = this.locales[0];

  constructor(private translate: TranslateService) {}

  /**
   * Never throws: a saved language that cannot be read is not a reason to stop
   * the app.
   *
   * WHY. CAPACITOR-1N (30/10/2026, iOS 26.6.1, 2.2.5): half a second after a
   * cold start on /home/explore, WebKit answered the read of the saved language
   * with « UnknownError: Attempt to get a record from database without an
   * in-progress transaction ». The message comes from WebKit's own IndexedDB
   * server, which Ionic Storage sits on: it loses the transaction when the
   * process that holds the database is stopped under it (WebKit bug 202705,
   * open since iOS 13). Nothing in our code can prevent that.
   *
   * What WAS ours: this method threw, and app.component awaits it first in
   * initializeApp, so everything after it never ran for that session: the deep
   * link listener (Stripe return, links from emails) and the first navigation.
   * The rejection went up unhandled, which is how it reached Sentry.
   *
   * So the read is tried a second time, on a freshly created store, then the
   * phone's language is used, as on a first start. The failure is still
   * reported, as a handled warning, so we see if it grows.
   */
  public async initStorage() {
    const savedLocale = await this.readTheSavedLocale();
    try {
      await this.setLocales(savedLocale || this.getDeviceLocale().code);
    } catch (error) {
      // The language is already applied when the write fails; only
      // remembering it for the next start is lost.
      this.reportAStoreThatFailed(error);
    }
  }

  private async readTheSavedLocale(): Promise<string | null> {
    for (let attempt = 1; ; attempt++) {
      try {
        await this.storage.create();
        return await this.storage.get(this.LANGUAGE_KEY);
      } catch (error) {
        if (attempt === 2) {
          this.reportAStoreThatFailed(error);
          return null;
        }
      }
    }
  }

  private reportAStoreThatFailed(error: unknown) {
    captureException(error, { level: 'warning', tags: { storage: 'preferred_language' } });
  }

  getCurrentLang(): string {
    return this.locale.lang;
  }

  async setLocales(localeKey: string) {
    let locale = this.locales.find((l) => l.code === localeKey);
    if (!locale) {
      locale = this.locales[0];
    }
    this.translate.use(locale.lang);
    this._locale = locale;
    rememberTheMapLanguage(locale.lang);
    await this.storage.set(this.LANGUAGE_KEY, localeKey);
  }

  private getDeviceLocale(): Locale {
    // Look for the exact match
    let locale = this.locales.find((l) => l.code === navigator.language);
    if (!locale) {
      // Look for the language match
      locale = this.locales.find((l) => l.lang === navigator.language.split('-')[0]);
    }
    if (!locale) {
      // If no match, use the first locale
      locale = this.locales[0];
    }
    return locale;
  }
}
